import { useCallback, useState } from 'react';
import type * as Types from '../../../graphql/types';
import {
  useCreateReportSubscriptionMutation,
  useDeleteReportSubscriptionMutation,
  useGetReportSubscriptionsByBrandQuery,
  useIsSubscribedToAutomatedReportsQuery,
  useSubscribeToAutomatedReportsMutation,
  useTriggerReportSubscriptionMutation,
  useUnsubscribeFromAutomatedReportsMutation,
  useUpdateReportSubscriptionMutation,
} from '../gql/_gen_/report-subscriptions.gql';

// UI Option types
export interface TimezoneOption {
  value: string;
  label: string;
}

export interface DeliveryTimeOption {
  value: string;
  label: string;
}

// Hook options type
export interface UseSubscriptionManagementOptions {
  brandCode?: string;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

// Default preferences for new subscriptions
export const DEFAULT_PREFERENCES: Types.ReportSubscriptionPreferencesInput = {
  includeMonday: false,
  includeTuesday: false,
  includeWednesday: false,
  includeThursday: false,
  includeFriday: false,
  includeSaturday: true,
  includeSunday: true,
  deliveryTime: '08:00',
  useAiNotes: false,
  description: null,
  recipientTeamMemberIds: null,
  startDate: null,
  endDate: null,
  message: null,
  priority: 0,
};

// Available timezones for the UI (sorted west to east by UTC offset)
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Puerto_Rico', label: 'Puerto Rico (No DST)' },
  { value: 'America/Halifax', label: 'Atlantic Time (AT)' },
  { value: 'America/St_Johns', label: 'Newfoundland Time (NT)' },
];

// Priority options (0-10, higher wins when multiple subscriptions trigger)
export interface PriorityOption {
  value: number;
  label: string;
}

export const PRIORITY_OPTIONS: PriorityOption[] = Array.from({ length: 11 }, (_, i) => ({
  value: i,
  label: i === 0 ? '0 (Default)' : String(i),
}));

// Time options for delivery preference
export const DELIVERY_TIME_OPTIONS: DeliveryTimeOption[] = [
  { value: '06:00', label: '6:00 AM' },
  { value: '07:00', label: '7:00 AM' },
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
];

export const useSubscriptionManagement = (options: UseSubscriptionManagementOptions = {}) => {
  const { brandCode, onSuccess, onError } = options;

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<string | null>(null);

  // Hooks - subscriptions are always fetched by brand code
  const {
    data: brandSubscriptionsData,
    loading: brandSubscriptionsLoading,
    error: brandSubscriptionsError,
    refetch: refetchBrandSubscriptions,
  } = useGetReportSubscriptionsByBrandQuery({
    variables: {
      brandCode: brandCode ?? '',
    },
    skip: !brandCode,
  });

  const subscriptionsData = brandSubscriptionsData?.reportSubscriptionsByBrand;

  const loading = brandSubscriptionsLoading;
  const error = brandSubscriptionsError;
  const refetch = refetchBrandSubscriptions;

  const {
    data: statusData,
    loading: statusLoading,
    refetch: refetchStatus,
  } = useIsSubscribedToAutomatedReportsQuery({
    variables: { brandCode: brandCode || '' },
    skip: !brandCode,
  });

  const [createSubscriptionMutation, { loading: creating }] = useCreateReportSubscriptionMutation();
  const [deleteSubscriptionMutation, { loading: deleting }] = useDeleteReportSubscriptionMutation();
  const [subscribeToReportsMutation, { loading: subscribing }] =
    useSubscribeToAutomatedReportsMutation();
  const [unsubscribeFromReportsMutation, { loading: unsubscribing }] =
    useUnsubscribeFromAutomatedReportsMutation();
  const [updateSubscriptionMutation, { loading: updatingPreferences }] =
    useUpdateReportSubscriptionMutation();
  const [triggerReportSubscriptionMutation, { loading: triggering }] =
    useTriggerReportSubscriptionMutation();

  const subscriptions = subscriptionsData || [];
  const isSubscribed = statusData?.isSubscribedToAutomatedReports || false;

  // Helper functions
  const handleSuccess = useCallback(
    (message: string) => {
      onSuccess?.(message);
    },
    [onSuccess],
  );

  const handleError = useCallback(
    (message: string) => {
      onError?.(message);
    },
    [onError],
  );

  // Actions
  const handleSubscribe = useCallback(
    async (
      targetBrandCode: string,
      timezone: string = 'America/Los_Angeles',
      preferences: Types.ReportSubscriptionPreferencesInput = DEFAULT_PREFERENCES,
    ) => {
      try {
        const result = await subscribeToReportsMutation({
          variables: { brandCode: targetBrandCode, timezone, preferences },
        });

        if (result.data?.subscribeToAutomatedReports?.success) {
          handleSuccess('Successfully subscribed to automated reports!');
          await refetchStatus();
          await refetch();
          setIsModalOpen(false);
          return { success: true, data: result.data.subscribeToAutomatedReports };
        } else {
          throw new Error(
            result.data?.subscribeToAutomatedReports?.message || 'Failed to subscribe',
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to subscribe to automated reports';
        handleError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [subscribeToReportsMutation, handleSuccess, handleError, refetchStatus, refetch],
  );

  const handleUnsubscribe = useCallback(
    async (targetBrandCode: string) => {
      try {
        const result = await unsubscribeFromReportsMutation({
          variables: { brandCode: targetBrandCode },
        });

        if (result.data?.unsubscribeFromAutomatedReports?.success) {
          handleSuccess('Successfully unsubscribed from automated reports');
          await refetchStatus();
          await refetch();
          return { success: true, data: result.data.unsubscribeFromAutomatedReports };
        } else {
          throw new Error(
            result.data?.unsubscribeFromAutomatedReports?.message || 'Failed to unsubscribe',
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to unsubscribe from automated reports';
        handleError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [unsubscribeFromReportsMutation, handleSuccess, handleError, refetchStatus, refetch],
  );

  const handleUpdatePreferences = useCallback(
    async (id: string, input: Types.UpdateReportSubscriptionInput) => {
      try {
        const result = await updateSubscriptionMutation({
          variables: {
            id,
            input,
          },
        });

        if (result.data?.updateReportSubscription?.success) {
          handleSuccess('Automated report preferences updated successfully!');
          await refetch();
          setIsPreferencesModalOpen(false);
          return { success: true, data: result.data.updateReportSubscription };
        } else {
          throw new Error(
            result.data?.updateReportSubscription?.message || 'Failed to update subscription',
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update preferences';
        handleError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [updateSubscriptionMutation, handleSuccess, handleError, refetch],
  );

  const handleCreateSubscription = useCallback(
    async (input: Types.CreateReportSubscriptionInput) => {
      try {
        const result = await createSubscriptionMutation({
          variables: { input },
        });

        if (result.data?.createReportSubscription?.success) {
          handleSuccess('Automated report subscription created successfully!');
          await refetch();
          setIsModalOpen(false);
          return { success: true, data: result.data.createReportSubscription };
        } else {
          throw new Error(
            result.data?.createReportSubscription?.message || 'Failed to create subscription',
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create subscription';
        handleError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [createSubscriptionMutation, handleSuccess, handleError, refetch],
  );

  const handleDeleteSubscription = useCallback(
    async (id: string) => {
      try {
        const result = await deleteSubscriptionMutation({
          variables: { id },
        });

        if (result.data?.deleteReportSubscription?.success) {
          handleSuccess('Automated report subscription deleted successfully');
          await refetchStatus();
          await refetch();
          return { success: true, data: result.data.deleteReportSubscription };
        } else {
          throw new Error(
            result.data?.deleteReportSubscription?.message || 'Failed to delete subscription',
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to delete subscription';
        handleError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [deleteSubscriptionMutation, handleSuccess, handleError, refetchStatus, refetch],
  );

  const handleTriggerReport = useCallback(
    async (id: string) => {
      try {
        const result = await triggerReportSubscriptionMutation({
          variables: { id },
        });

        if (result.data?.triggerReportSubscription?.success) {
          handleSuccess('Report generation triggered successfully!');
          return { success: true, data: result.data.triggerReportSubscription };
        } else {
          throw new Error(
            result.data?.triggerReportSubscription?.message || 'Failed to trigger report',
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to trigger report generation';
        handleError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [triggerReportSubscriptionMutation, handleSuccess, handleError],
  );

  // UI helpers
  const openSubscriptionModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeSubscriptionModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSubscription(null);
  }, []);

  const openPreferencesModal = useCallback((subscriptionId?: string) => {
    if (subscriptionId) {
      setSelectedSubscription(subscriptionId);
    }
    setIsPreferencesModalOpen(true);
  }, []);

  const closePreferencesModal = useCallback(() => {
    setIsPreferencesModalOpen(false);
    setSelectedSubscription(null);
  }, []);

  // Get subscription for current brand code
  const currentSubscription = brandCode
    ? subscriptions.find(
        (sub): sub is NonNullable<typeof sub> => sub?.brandCode === brandCode && sub.isActive,
      )
    : null;

  // Get subscription by ID
  const getSubscriptionById = useCallback(
    (id: string) => subscriptions.find((sub): sub is NonNullable<typeof sub> => sub?.id === id),
    [subscriptions],
  );

  // Check if any operations are in progress
  const isOperating =
    creating || deleting || subscribing || unsubscribing || updatingPreferences || triggering;

  return {
    // Data
    subscriptions,
    currentSubscription,
    isSubscribed: brandCode ? isSubscribed : false,
    selectedSubscription: selectedSubscription ? getSubscriptionById(selectedSubscription) : null,

    // Loading states
    loading: loading || statusLoading,
    isOperating,
    creating,
    deleting,
    subscribing,
    unsubscribing,
    updatingPreferences,
    triggering,

    // Errors
    error,

    // Actions
    handleSubscribe,
    handleUnsubscribe,
    handleUpdatePreferences,
    handleCreateSubscription,
    handleDeleteSubscription,
    handleTriggerReport,
    refetch,

    // Modal states
    isModalOpen,
    isPreferencesModalOpen,
    openSubscriptionModal,
    closeSubscriptionModal,
    openPreferencesModal,
    closePreferencesModal,

    // Helpers
    getSubscriptionById,

    // Constants
    DEFAULT_PREFERENCES,
    TIMEZONE_OPTIONS,
    DELIVERY_TIME_OPTIONS,
    PRIORITY_OPTIONS,
  };
};
